import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

import DataTable from '../../../sharedModules/dataTable/DataTable/DataTable';
import { IDataTableColumn } from '../../../sharedModules/dataTable/DataTable/model';
import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

const getStatusChip = (status: string) => {
  const statusConfig: Record<string, { label: string; color: any; icon: any }> = {
    matched: { label: 'Matched', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    discrepancy: { label: 'Discrepancy', color: 'warning', icon: <WarningIcon fontSize="small" /> },
    unmatched: { label: 'Unmatched', color: 'error', icon: <CancelIcon fontSize="small" /> },
    approved: { label: 'Approved', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
    rejected: { label: 'Rejected', color: 'error', icon: <CancelIcon fontSize="small" /> },
    parsing_statement: { label: 'Parsing Statement', color: 'info', icon: <HourglassEmptyIcon fontSize="small" /> },
    unvalidated: { label: 'Unvalidated', color: 'default', icon: <HelpOutlineIcon fontSize="small" /> },
  };

  const config = statusConfig[status] || statusConfig.unvalidated;
  return <Chip label={config.label} color={config.color} size="small" icon={config.icon} />;
};

const columns: IDataTableColumn<any>[] = [
  {
    name: 'Vendor Name',
    selector: 'resolved_vendor_name',
    sortable: true,
    wrap: true,
    format: (row: any) => row.resolved_vendor_name || '-',
  },
  {
    name: 'Received At',
    selector: 'email_received_at',
    sortable: true,
    format: (row: any) => {
      const date = row.email_received_at || row.created_at;
      if (!date) return '-';
      return new Date(date).toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
  {
    name: 'Total Amount',
    selector: 'line_items_total',
    sortable: true,
    format: (row: any) => {
      const total = parseFloat(row.line_items_total);
      if (!total && total !== 0) return '-';
      return `$${total.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
  },
  {
    name: 'Validation Status',
    selector: 'validation_status',
    sortable: true,
    format: (row: any) => getStatusChip(row.display_status || row.validation_status || 'unvalidated'),
  },
  {
    name: 'Line Items',
    selector: 'line_items_count',
    sortable: false,
    format: (row: any) => row.line_items_count || 0,
  },
  {
    name: 'ServMan',
    selector: 'servman_matched_count',
    sortable: false,
    format: (row: any) => {
      const matched = row.servman_matched_count || 0;
      const total = row.line_items_count || 0;
      if (total === 0) return '-';
      const color = matched === total ? 'success' : matched > 0 ? 'warning' : 'error';
      return <Chip label={`${matched}/${total}`} color={color} size="small" variant="outlined" />;
    },
  },
  {
    name: 'DocuWare',
    selector: 'docuware_matched_count',
    sortable: false,
    format: (row: any) => {
      const matched = row.docuware_matched_count || 0;
      const total = row.line_items_count || 0;
      if (total === 0) return '-';
      const color = matched === total ? 'success' : matched > 0 ? 'warning' : 'error';
      return <Chip label={`${matched}/${total}`} color={color} size="small" variant="outlined" />;
    },
  },
  {
    name: 'Unmatched',
    selector: 'unmatched_count',
    sortable: false,
    format: (row: any) => {
      const unmatched = row.unmatched_count || 0;
      if ((row.line_items_count || 0) === 0) return '-';
      if (unmatched === 0) return <Chip label="0" color="success" size="small" variant="outlined" />;
      return <Chip label={String(unmatched)} color="error" size="small" icon={<CancelIcon />} />;
    },
  },
];

const VendorInvoiceListPage: React.FunctionComponent = () => {
  const history = useHistory();
  const { showErrorSnackBar } = useContext(SnackBarContext);

  const [invoices, setInvoices] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('discrepancy');
  const [loading, setLoading] = useState<boolean>(false);

  const fetchInvoices = async (search = '', status = 'all') => {
    setLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      if (status !== 'all') params.validation_status = status;

      const response: any = await axios.get('/vendors/vendor_invoices', { params });
      setInvoices(response.data || []);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors?.[0] || 'Failed to fetch vendor statements';
      showErrorSnackBar(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchInvoices(searchTerm, statusFilter);
  };

  const handleStatusChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    fetchInvoices(searchTerm, newStatus);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  useEffect(() => {
    fetchInvoices('', 'discrepancy');
  }, []);

  return (
    <Box style={{ width: '100%' }}>
      <Box>
        <Grid justifyContent="space-between" container spacing={3}>
          <Grid item>
            <Typography variant="h5" component="h5" style={{ marginTop: 5 }}>
              Vendor Statements
            </Typography>
          </Grid>
          <Grid item>
            <Button
              onClick={() => history.push('/vendors/invoices/upload')}
              variant="contained"
              startIcon={<UploadFileIcon />}
              style={{ marginTop: 5 }}
            >
              Upload Invoice
            </Button>
          </Grid>
        </Grid>
        <br />
        <Grid container spacing={2} alignItems="flex-start" onKeyDown={handleKeyPress} style={{ marginBottom: 16 }}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Search by Vendor or Invoice Number"
              variant="standard"
              style={{ width: '100%', marginTop: 8 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Enter vendor name or invoice number"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl variant="standard" style={{ width: '100%', marginTop: 8 }}>
              <InputLabel>Validation Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value as string)}
                label="Validation Status"
              >
                <MenuItem value="all">All Invoices</MenuItem>
                <MenuItem value="unvalidated">Unvalidated</MenuItem>
                <MenuItem value="matched">Matched</MenuItem>
                <MenuItem value="discrepancy">Discrepancy</MenuItem>
                <MenuItem value="unmatched">Unmatched</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button onClick={handleSearch} variant="contained" style={{ marginTop: 20 }}>
              Search
            </Button>
          </Grid>
        </Grid>
        <Card>
          <CardContent>
            <DataTable
              title="Invoices"
              columns={columns}
              data={invoices}
              onRowClicked={(row) => history.push(`/vendors/invoices/${row.id}`)}
              striped
              dense
              highlightOnHover
              pointerOnHover
              noHeader
              pagination
              paginationPerPage={25}
              paginationRowsPerPageOptions={[25, 50, 100]}
              progressPending={loading}
            />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default VendorInvoiceListPage;
